import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CampaignObject, MarketoProgramObject, ParentCampaignObject } from "../types/APITypes";
import { assertNotNull } from "../util/helpers/assert";
import { useGetParentCampaigns } from "../hooks/useGetParentCampaigns";
import { useGetMarketoPrograms } from "../hooks/useGetMarketoPrograms";
import { useGetCampaigns } from "../hooks/useGetCampaigns";


interface GlobalAppContextType {

    campaigns: CampaignObject[];
    setCampaigns: (c : CampaignObject[]) => void;

    parentCampaigns: ParentCampaignObject[];
    setParentCampaigns: (p : ParentCampaignObject[]) => void;

    programs: MarketoProgramObject[];
    setPrograms: (p : MarketoProgramObject[]) => void;

    refresh: boolean;
    setRefresh: (r: boolean) => void;
    
}



const GlobalAppContext = createContext<GlobalAppContextType | null>(null);

export const useGlobalAppContext = () =>     
    assertNotNull(
        useContext(GlobalAppContext),
        "GlobalAppContext.useContext must be called from a child of GlobalAppContext.Provider"
    )

export const Provider = ( {children} : React.PropsWithChildren<{}> ) => {

    const [parentCampaigns, setParentCampaigns] = useState<ParentCampaignObject[]>([] as ParentCampaignObject[]);
    const [programs, setPrograms] = useState<MarketoProgramObject[]>([] as MarketoProgramObject[]);
    const [campaigns, setCampaigns] = useState<CampaignObject[]>([] as CampaignObject[]);
    const [refresh, setRefresh] = useState(false);

    const parentsResponse = useGetParentCampaigns();
    const programsResponse = useGetMarketoPrograms();
    const campaignsResponse = useGetCampaigns();

    useEffect(() => {

        if (parentsResponse?.value !== undefined) {
            setParentCampaigns(parentsResponse.value);
        }
    }, [parentsResponse])

    useEffect(() => {

        if (programsResponse?.value !== undefined) {
            setPrograms(programsResponse.value);
        }
    }, [programsResponse])

    useEffect(() => {

        if (campaignsResponse?.value !== undefined) {
            setCampaigns(campaignsResponse.value)
        }
    }, [campaignsResponse])

    useEffect(() => {
        if (refresh) {
            campaignsResponse.getCall();
            parentsResponse.getCall();
            programsResponse.getCall();
            setRefresh(false);
        }
    }, [refresh])


    const providerValue = useMemo(() => ({
        campaigns, setCampaigns,
        parentCampaigns, setParentCampaigns,
        programs, setPrograms,
        refresh, setRefresh
    }), [campaigns, parentCampaigns, programs])

    return (
        <GlobalAppContext.Provider value={providerValue}>
            {children}
        </GlobalAppContext.Provider>
    )
}

export default GlobalAppContext;